import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useCcrcTypeAProducts } from './use-ccrc-type-a-products';

import { SessionType } from '@shared';
import {
  selectClient,
  selectSession,
  selectSessionAdvisor,
  putMultipleFundingSourceForClientByClientIdRequest,
} from '@/model';
import {
  buildCcrcTypeAPolicy,
  computeCcrcPricing,
  type CcrcCoverageType,
} from '../lib/ccrc-type-a-pricing';

export function useCcrcTypeAFlow(clientId: string) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const client = useSelector(selectClient)!;
  const { sessionType } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);
  const { products, isLoading, isError } = useCcrcTypeAProducts();

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const partner = client.mutableClientPartner ?? null;
  const hasPartner = Boolean(partner);
  const clientAge = client.intakeSurvey.clientAge;
  const partnerAge = partner?.intakeSurvey.clientAge;

  const goToFundingSources = () => {
    navigate(`/clients/${clientId}/onboarding/funding-sources`);
  };

  const handleAddPlan = (product: CcrcTypeAProduct, coverage: CcrcCoverageType) => {
    const pricing = computeCcrcPricing({ product, coverage, clientAge, partnerAge, hasPartner });
    const advisorId = isAdvisorSession ? advisor?.advisorId : undefined;

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: buildCcrcTypeAPolicy({
          product,
          entranceFee: pricing.primaryEntranceFee,
          monthlyFee: pricing.monthlyPerPerson,
          addedByAdvisorId: advisorId,
        }),
        fundingSourceId: null,
      }),
    );

    if (coverage === 'couple' && partner && pricing.partnerEntranceFee != null) {
      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: partner.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: buildCcrcTypeAPolicy({
            product,
            entranceFee: pricing.partnerEntranceFee,
            monthlyFee: pricing.monthlyPerPerson,
            addedByAdvisorId: advisorId,
          }),
          fundingSourceId: null,
        }),
      );
    }

    goToFundingSources();
  };

  return {
    products,
    client,
    partner,
    isLoading,
    isError,
    handleAddPlan,
    onClose: goToFundingSources,
  };
}
